import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Button, Spinner } from "react-bootstrap";
import {
  FaRupeeSign,
  FaClock,
  FaUsers,
  FaBookOpen,
  FaDownload,
  FaChevronRight,
  FaChevronDown,
  FaStar,
  FaArrowLeft,
  FaCertificate,
} from "react-icons/fa";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import CourseService from "../../services/courseService";
import "../../styles/courseDetails.css";

const CourseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModules, setActiveModules] = useState([]);
  const [activeFaq, setActiveFaq] = useState(null);
  const [studentCount, setStudentCount] = useState(0);
  const [relatedCourses, setRelatedCourses] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  useEffect(() => {
    loadCourse();
    window.scrollTo(0, 0);
  }, [id]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      const response = await CourseService.getCourseById(id);
      let courseData = response.data;
      if (response.data && response.data.data) {
        courseData = response.data.data;
      }
      setCourse(courseData);

      // Calculate total students: base_student_count + actual enrolled_students
      const totalStudents = 
        (courseData?.base_student_count || 0) +
        (courseData?.enrolled_students || 0);
      setStudentCount(totalStudents);

      // Parse modules from long_description
      if (courseData?.long_description) {
        parseModules(courseData.long_description);
      }

      // Load related courses (excluding current course)
      await loadRelatedCourses(courseData?.id);
    } catch (error) {
      console.error("Error loading course:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedCourses = async (currentCourseId) => {
    try {
      setLoadingRelated(true);
      const response = await CourseService.getAllCourses();
      let allCourses = [];
      if (response && response.data && Array.isArray(response.data)) {
        allCourses = response.data;
      } else if (response && Array.isArray(response)) {
        allCourses = response;
      }
      
      // Filter out current course and get top 3 courses
      const filtered = allCourses
        .filter(course => course.id !== parseInt(currentCourseId))
        .slice(0, 3);
      
      setRelatedCourses(filtered);
    } catch (error) {
      console.error("Error loading related courses:", error);
      setRelatedCourses([]);
    } finally {
      setLoadingRelated(false);
    }
  };

  const parseModules = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const h3Elements = doc.querySelectorAll("h3");

    const modules = [];
    h3Elements.forEach((h3) => {
      let content = "";
      let nextElement = h3.nextElementSibling;
      while (nextElement && nextElement.tagName !== "H3") {
        if (
          nextElement.tagName === "P" ||
          nextElement.tagName === "UL" ||
          nextElement.tagName === "OL"
        ) {
          content += nextElement.outerHTML;
        }
        nextElement = nextElement.nextElementSibling;
      }
      modules.push({
        title: h3.textContent,
        content: content,
      });
    });

    setActiveModules(modules.map((_, index) => ({ index, isOpen: false })));
    return modules;
  };

  const getModules = () => {
    if (!course?.long_description) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(course.long_description, "text/html");
    const h3Elements = doc.querySelectorAll("h3");

    const modules = [];
    h3Elements.forEach((h3) => {
      let content = "";
      let nextElement = h3.nextElementSibling;
      while (nextElement && nextElement.tagName !== "H3") {
        if (
          nextElement.tagName === "P" ||
          nextElement.tagName === "UL" ||
          nextElement.tagName === "OL"
        ) {
          content += nextElement.outerHTML;
        }
        nextElement = nextElement.nextElementSibling;
      }
      modules.push({
        title: h3.textContent,
        content: content,
      });
    });
    return modules;
  };

  const toggleModule = (index) => {
    setActiveModules((prev) =>
      prev.map((item) =>
        item.index === index ? { ...item, isOpen: !item.isOpen } : item,
      ),
    );
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleEnroll = () => {
    navigate(`/register?course=${course?.id}`);
  };

  const handleDownloadSyllabus = async () => {
    try {
      await CourseService.downloadSyllabus(id);
    } catch (error) {
      console.error("Error downloading syllabus:", error);
    }
  };

  // Format student count with + for large numbers
  const formatStudentCount = (count) => {
    if (count > 1000) {
      return `${Math.floor(count / 1000)}k+`;
    }
    return count;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const faqs = [
    {
      question: "What are the prerequisites for this course?",
      answer:
        "Basic computer knowledge is recommended. No prior programming experience is required as we start from basics.",
    },
    {
      question: "Will I get a certificate after completion?",
      answer:
        "Yes, you will receive a industry-recognized certificate upon successful completion of the course.",
    },
    {
      question: "Is there any placement assistance provided?",
      answer:
        "Yes, we provide 100% placement assistance including resume building, mock interviews, and direct referrals to our partner companies.",
    },
    {
      question: "Can I attend a demo class before enrolling?",
      answer:
        "Yes, we offer free demo classes. Contact our support team to schedule your demo session.",
    },
    {
      question: "What if I miss a class?",
      answer:
        "All classes are recorded and available in your dashboard. You can watch them anytime.",
    },
    {
      question: "Is EMI option available for fee payment?",
      answer:
        "Yes, we offer flexible EMI options with 0% interest. Contact our admission team for details.",
    },
  ];

  if (loading) {
    return (
      <div>
        <PublicNavbar />
        <div className="course-details-loading text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading course details...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!course) {
    return (
      <div>
        <PublicNavbar />
        <div className="course-details-not-found text-center py-5">
          <h3>Course not found</h3>
          <Button variant="primary" onClick={() => navigate("/courses")}>
            Back to Courses
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const modules = getModules();

  return (
    <div className="course-details-wrapper">
      <PublicNavbar />

      {/* Hero Section */}
      <div className="course-details-hero">
        <Container>
          <Button
            variant="light"
            className="course-details-back-button mb-4"
            onClick={() => navigate("/courses")}
          >
            <FaArrowLeft className="me-2" /> Back to Courses
          </Button>

          <Row className="align-items-center">
            <Col lg={5} className="mb-4 mb-lg-0">
              <div className="course-details-image-large">
                {course.image_url ? (
                  <img src={course.image_url} alt={course.name} />
                ) : (
                  <div className="course-details-image-placeholder">
                    <FaBookOpen size={80} />
                  </div>
                )}
              </div>
            </Col>

            <Col lg={7}>
              <h1 className="course-details-title">{course.name}</h1>
              <div className="course-details-stats">
                <div className="course-details-stat-card">
                  <FaRupeeSign className="course-details-stat-icon" />
                  <div>
                    <span className="course-details-stat-value">
                      {formatCurrency(course.price)}
                    </span>
                    <span className="course-details-stat-label">
                      Course Fee
                    </span>
                  </div>
                </div>
                <div className="course-details-stat-card">
                  <FaClock className="course-details-stat-icon" />
                  <div>
                    <span className="course-details-stat-value">
                      {course.duration}
                    </span>
                    <span className="course-details-stat-label">Duration</span>
                  </div>
                </div>
                <div className="course-details-stat-card">
                  <FaUsers className="course-details-stat-icon" />
                  <div>
                    <span className="course-details-stat-value">
                      {formatStudentCount(studentCount)}+
                    </span>
                    <span className="course-details-stat-label">
                      Students Enrolled
                    </span>
                  </div>
                </div>
              </div>

              <div className="course-details-actions-hero">
                <Button
                  variant="primary"
                  size="lg"
                  className="course-details-enroll-btn-hero"
                  onClick={handleEnroll}
                >
                  Enroll Now
                </Button>
                {course.syllabus_name && (
                  <Button
                    variant="outline-light"
                    size="lg"
                    className="course-details-download-btn"
                    onClick={handleDownloadSyllabus}
                  >
                    <FaDownload className="me-2" /> Download Syllabus
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </Container>
        <div className="course-details-hero-wave-bottom">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 260"
            preserveAspectRatio="none"
          >
            <path
              fill="#ffffff"
              fillOpacity="1"
              d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,170.7C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>
      </div>

      <Container className="course-details-container py-5">
        {/* Course Overview Section */}
        <div className="course-details-section-card">
          <h2 className="course-details-section-title">
            <FaBookOpen className="course-details-title-icon" />
            Course Overview
          </h2>
          <div className="course-details-overview-content">
            <p>{course.description}</p>
          </div>
        </div>

        {/* What You'll Learn Section */}
        <div className="course-details-section-card">
          <h2 className="course-details-section-title">
            <FaBookOpen className="course-details-title-icon" />
            What You'll Learn
          </h2>

          {modules.length > 0 ? (
            <div className="course-details-modules-accordion">
              {modules.map((module, index) => {
                const isOpen =
                  activeModules.find((m) => m.index === index)?.isOpen || false;
                return (
                  <div key={index} className="course-details-module-item">
                    <div
                      className="course-details-module-header"
                      onClick={() => toggleModule(index)}
                    >
                      <div className="course-details-module-header-left">
                        {isOpen ? (
                          <FaChevronDown className="course-details-chevron" />
                        ) : (
                          <FaChevronRight className="course-details-chevron" />
                        )}
                        <span className="course-details-module-number">
                          Module {index + 1}
                        </span>
                        <h3 className="course-details-module-title">
                          {module.title}
                        </h3>
                      </div>
                    </div>
                    {isOpen && (
                      <div className="course-details-module-content">
                        <div
                          dangerouslySetInnerHTML={{ __html: module.content }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="course-details-no-modules">
              <p>Course curriculum is being prepared. Check back soon!</p>
            </div>
          )}
        </div>

        {/* Why Choose This Course Section */}
        <div className="course-details-section-card">
          <h2 className="course-details-section-title">
            <FaStar className="course-details-title-icon" />
            Why Choose This Course?
          </h2>
          <Row className="g-4">
            <Col md={4}>
              <div className="course-details-feature-box">
                <FaCertificate className="course-details-feature-icon" />
                <h4>Industry Recognized Certificate</h4>
                <p>Get certified and boost your career prospects</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="course-details-feature-box">
                <FaUsers className="course-details-feature-icon" />
                <h4>Expert Instructors</h4>
                <p>
                  Learn from industry professionals with years of experience
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="course-details-feature-box">
                <FaBookOpen className="course-details-feature-icon" />
                <h4>Hands-on Projects</h4>
                <p>Build real-world projects to strengthen your portfolio</p>
              </div>
            </Col>
          </Row>
        </div>

        {/* FAQ Section */}
        <div className="course-details-section-card">
          <h2 className="course-details-section-title">
            <FaStar className="course-details-title-icon" />
            Frequently Asked Questions
          </h2>
          <div className="course-details-faq-accordion">
            {faqs.map((faq, index) => (
              <div key={index} className="course-details-faq-item">
                <div
                  className="course-details-faq-question"
                  onClick={() => toggleFaq(index)}
                >
                  {activeFaq === index ? <FaChevronDown /> : <FaChevronRight />}
                  <span>{faq.question}</span>
                </div>
                {activeFaq === index && (
                  <div className="course-details-faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Related Courses Section */}
        <div className="course-details-section-card">
          <h2 className="course-details-section-title">
            <FaBookOpen className="course-details-title-icon" />
            You May Also Like
          </h2>
          {loadingRelated ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : relatedCourses.length === 0 ? (
            <p className="text-muted text-center py-4">No related courses found.</p>
          ) : (
            <Row className="g-4">
              {relatedCourses.map((relCourse) => {
                const totalStudents = (relCourse.base_student_count || 0) + (relCourse.enrolled_students || 0);
                return (
                  <Col md={4} key={relCourse.id}>
                    <div className="course-details-related-card">
                      {relCourse.image_url ? (
                        <img
                          src={relCourse.image_url}
                          alt={relCourse.name}
                          className="course-details-related-img"
                        />
                      ) : (
                        <div className="course-details-related-img-placeholder">
                          <FaBookOpen size={40} />
                        </div>
                      )}
                      <h4>{relCourse.name}</h4>
                      <div className="course-details-related-meta">
                        <span>
                          <FaClock /> {relCourse.duration}
                        </span>
                        <span>
                          <FaRupeeSign /> {formatCurrency(relCourse.price)}
                        </span>
                        <span>
                          <FaUsers /> {formatStudentCount(totalStudents)} Students
                        </span>
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="course-details-related-btn"
                        onClick={() => navigate(`/courses/${relCourse.id}`)}
                      >
                        View Course
                      </Button>
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}
        </div>

        {/* CTA Section */}
        <div className="course-details-cta-section">
          <div className="course-details-cta-content">
            <h2>Ready to Start Your Learning Journey?</h2>
            <p>
              Join thousands of successful students who have transformed their
              careers
            </p>
            <div className="course-details-cta-buttons">
              <Button
                variant="primary"
                size="lg"
                className="course-details-cta-enroll"
                onClick={handleEnroll}
              >
                Enroll Now for {formatCurrency(course.price)}
              </Button>
              &nbsp;&nbsp;&nbsp;
              <Button
                variant="outline-light"
                size="lg"
                className="course-details-cta-counselor"
                onClick={() => navigate("/contact")}
              >
                Talk to Counselor
              </Button>
            </div>
          </div>
        </div>
      </Container>

      <Footer />
    </div>
  );
};

export default CourseDetails;